import { HierarchyUtils, ComponentIds, Vector, Radians } from '@inter-ikea-core/core';
import EventComponent from '../../components/EventComponent';
import { PlannerEcsMultiSystem } from '../System';
import { ProductType } from '../../../types';
import LightSourceMetaComponent from '../../components/LightSourceMetaComponent';
import { defaultRenderSettings } from '../../../config/defaultRenderSettings';
import ToggleDoorsComponent from '../../components/ToggleDoorsComponent';
import { product } from 'ramda';
import SpotLightSource from './SpotLightSource';
import { LineLightSource } from './LineLightSource';
/**
 * When adding and removing lights from the scene this system will
 * create appropriate light sources for visual effect
 * @param coreApi CoreApi
 * @param maxNrAllowedLightSources Because of limitations in viewer we set a max
 * amount of light sources to create. If this parameter e.g. is set to 6 no more
 * than 6 light sources will be created no matter how many lights you add to the
 * scene. The number includes both spotlights and line lights (interior lights).
 * At about 7 light sources they no longer emit light and are only performance
 * draining.
 */
export class NightLightsSystem extends PlannerEcsMultiSystem {
    constructor(coreApi, maxNrAllowedLightSources) {
        super(coreApi, 'NightLightsSystem', [
            ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID,
            EventComponent.id
        ]);
        this.spotLights = [];
        this.lineLights = [];
        this.renderSettings = defaultRenderSettings;
        this.maxNrOfLights = maxNrAllowedLightSources;
        this.products = [];
        this.discoMode = null;
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID) {
            if (!newComponent.entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
                HierarchyUtils.descendants(entity).filter(e => this.coreApi.entities.isProductType(e, ProductType.PAX_INNER_LIGHT) ||
                    this.coreApi.entities.isProductType(e, ProductType.PAX_INNER_LIGHT_OVERSIDAN)).length > 0) {
                // Update when inner lights are being dragged since they can auto adapt to different sizes
                this.recreateLights();
            }
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        if (newComponent.typeId === EventComponent.id &&
            ['history'].includes(newComponent.event)) {
            if (this.isEnabled) {
                this.recreateLights();
            }
        }
    }
    onRemove() { }
    disable() {
        if (this.isEnabled) {
            this.isEnabled = false;
            this.coreApi.lights?.setLightScene();
            this.removeSpotLights();
            this.removeLineLights();
        }
    }
    enable() {
        this.isEnabled = true;
        this.coreApi.lights?.setDarkScene();
        this.recreateLights();
    }
    setMarketProducts(marketProduct) {
        this.products = marketProduct;
    }
    recreateLights() {
        // Update lights sources in scene
        this.removeLineLights();
        this.removeSpotLights();
        if (this.isEnabled) {
            this.createLights([...this.getOuterLights(), ...this.getInnerLights()], this.coreApi);
        }
    }
    enableDiscoMode() {
        this.enable();
        this.discoMode = setInterval(() => {
            [...this.lineLights, ...this.spotLights].forEach(light => {
                light.setColor(this.getRandomColor());
            });
        }, 500);
    }
    disableDiscoMode() {
        this.discoMode && clearInterval(this.discoMode);
        this.disable();
    }
    getRandomColor() {
        const colors = [
            { r: 1, g: 0, b: 0 },
            { r: 0, g: 1, b: 0 },
            { r: 0, g: 0, b: 1 }
        ];
        return colors[this.getRandomNbr(0, colors.length - 1)];
    }
    getRandomNbr(min, max) {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }
    getLightsInScene() {
        const allLightsInScene = [];
        const currentLightsInScene = this.coreApi.entities.getByComponent(ComponentIds.LIGHT_COMPONENT_ID);
        if (currentLightsInScene) {
            allLightsInScene.push(...currentLightsInScene);
        }
        return allLightsInScene;
    }
    getOuterLights() {
        const allLights = this.getLightsInScene();
        return allLights.filter((e) => e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'outer');
    }
    getInnerLights() {
        const allLights = this.getLightsInScene();
        return allLights.filter((e) => e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner' ||
            e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner-oversidan');
    }
    removeSpotLights() {
        this.spotLights.forEach(light => light.remove());
        this.spotLights = [];
    }
    removeLineLights() {
        this.lineLights.forEach(light => light.remove());
        this.lineLights = [];
    }
    /**
     * Get how many light sources we currently use
     * @returns Nr of light sources in the scene
     */
    getCurrentNrOfLights() {
        return this.spotLights.length + this.lineLights.length;
    }
    createLights(lightEntities, coreApi) {
        lightEntities.forEach(entity => {
            // If we hit the current max nr of light sources we do not need to create another. Only performance draining
            if (this.maxNrOfLights > this.getCurrentNrOfLights()) {
                const hasCoveringFront = this.frontIsCoveringLight(entity, coreApi);
                //If light has a covering front do not create it
                if (hasCoveringFront) {
                    return;
                }
                const viewerModelRef = entity
                    .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                    ?.viewerObject?.getViewerObjectId();
                const metadata = entity.getComponent(LightSourceMetaComponent.id);
                switch (metadata?.lightSourceType) {
                    case 'line':
                        this.createLineLight(entity, viewerModelRef, metadata);
                        break;
                    case 'spot':
                        this.createSpotLight(entity, coreApi, viewerModelRef, metadata);
                }
            }
            else {
                this.coreApi.emit.onTooManyLightsAdded();
            }
        });
    }
    createLineLight(entity, viewerModelRef, metadata) {
        if (!viewerModelRef)
            return;
        const size = entity.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)
            ?.size;
        const intensity = defaultRenderSettings.darkMode.lineLight.intensity;
        const angle = metadata?.lightAngle || defaultRenderSettings.darkMode.lineLight.angle;
        this.lineLights.push(new LineLightSource(this.coreApi.ipexViewer, viewerModelRef, intensity, {
            r: this.renderSettings.spotLight.color.r / 255,
            g: this.renderSettings.spotLight.color.g / 255,
            b: this.renderSettings.spotLight.color.b / 255
        }, size ? size.X / 1000 : 0, true, angle));
    }
    createSpotLight(entity, coreApi, viewerModelRef, metadata) {
        const lightConnectors = entity
            .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
            ?.connectors.filter(connector => connector.tags().includes('light-source')) || [];
        if (!viewerModelRef || lightConnectors.length === 0) {
            // eslint-disable-next-line no-console
            console.error(`No spotlight connector for ${product}. Add one to be able to create a light`);
            return;
        }
        const entityObjectId = coreApi.entities.getObjectId(entity);
        const visible = entityObjectId && coreApi.ipexViewer.get_visible(entityObjectId);
        const lightSourceWidth = metadata?.lightSourceWidth || defaultRenderSettings.darkMode.spotLight.width;
        const lightAngle = metadata?.lightAngle || defaultRenderSettings.darkMode.spotLight.angle;
        const intensity = metadata?.lightIntensity || this.renderSettings.darkMode.spotLight.intensity;
        const lightSourcePosition = lightConnectors[0].transform.position;
        const lightSourceDirection = lightConnectors[0].transform.up.rotateAroundAxis(new Vector(0, 0, 1), new Radians(Math.PI));
        this.spotLights.push(new SpotLightSource(this.coreApi.ipexViewer, viewerModelRef, intensity, {
            r: this.renderSettings.spotLight.color.r / 255,
            g: this.renderSettings.spotLight.color.g / 255,
            b: this.renderSettings.spotLight.color.b / 255
        }, lightSourcePosition, lightSourceWidth, lightSourceDirection, !!visible, lightAngle));
    }
    /**
     * Checks if light entity has a front/door covering it. Glass doors is not considered as covering.
     * @param lightEntity A light entity
     * @param coreApi CoreApi
     * @returns true if a door/front is covering the light entity
     */
    /**
     * Determines if a light entity is covered by a non-glass front/door.
     *
     * This method checks if there is a front/door positioned in front of the light that would block its visibility.
     * The check is performed by:
     * 1. First verifying the light is not an outer light (which are never considered covered)
     * 2. Casting a ray from 5cm below the light forward to detect any fronts
     * 3. For any detected front, checking if it's:
     *    - Not hidden (via door toggle state)
     *    - Not a glass door (which don't block light)
     *
     * @param lightEntity - The entity representing the light fixture to check
     * @param coreApi - Core API instance providing access to entity and product information
     * @returns boolean - true if the light is covered by a solid (non-glass) front, false otherwise
     */
    frontIsCoveringLight(lightEntity, coreApi) {
        if (lightEntity.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'outer') {
            return false;
        }
        // Position 5 cm below light
        const positionBelow = coreApi.entities
            .getWorldPosition(lightEntity)
            .plus(new Vector(0, -50, 0));
        const lightFrame = HierarchyUtils.findWithComponentInUpwardsHierarchy(lightEntity, ComponentIds.FRAME_COMPONENT_ID) ?? undefined;
        const coveringFronts = coreApi.entities
            .getEntitiesInDirection(positionBelow, new Vector(0, 0, 1), lightFrame)
            .filter(hit => hit.entity.hasComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID));
        if (coveringFronts.length > 0) {
            const doorHidden = coveringFronts[0].entity.getComponent(ToggleDoorsComponent.id)?.doorsHidden;
            const productRef = coreApi.entities.getProductRef(coveringFronts[0].entity);
            const isGlassDoor = coreApi.products.isGlassDoor(productRef || '', this.products);
            return !doorHidden && !isGlassDoor;
        }
        return false;
    }
}
//# sourceMappingURL=NightLightsSystem.js.map